import { useMemo } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import { Pencil } from '@/components/icons'
import { Alert } from '@/components/ui/alert/alert'
import { Button } from '@/components/ui/button'
import { PageCard, PageLayout, PageTwoColumn } from '@/components/ui/page'
import { Table } from '@/components/ui/table/table'
import { Text } from '@/components/ui/text'

import { toIdr } from '@/libs/utils/format'

import { itemMasterDetail } from '@/features/item/api'
import { ItemMasterTypeBadge } from '@/features/item/components/common'
import { useItemCategory } from '@/features/item/hook'
import { UnitConversionPreviewTable } from '@/features/unit/components/unit-common'
import { useUnit } from '@/features/unit/hook'

export const Route = createFileRoute('/_app/inventory/master/$id/')({
	loader: async ({ context: { qc }, params: { id } }) => {
		await qc.ensureQueryData(itemMasterDetail.qry({ id }))
	},
	component: Component,
})

function Component() {
	const { item } = useItem()

	return (
		<PageLayout.Root>
			<PageLayout.Header
				breadcrumbs={[
					['/inventory', 'Inventory'],
					['/inventory/master', 'Master Bahan'],
					[Route.fullPath, item.name],
				]}
			/>
			<PageTwoColumn>
				<PageTwoColumn.Main>
					<GeneralSection />
					<UnitSection />
				</PageTwoColumn.Main>
				<PageTwoColumn.Sidebar>
					{item.type === 'semi' ? <RecipeSection /> : null}
					<OutletSection />
				</PageTwoColumn.Sidebar>
			</PageTwoColumn>
		</PageLayout.Root>
	)
}

function GeneralSection() {
	const { id, item } = useItem()
	const itemCategory = useItemCategory()
	const unitData = useUnit()

	const category = useMemo(
		() => itemCategory.byId(item.categoryId),
		[item.categoryId, itemCategory.byId]
	)

	const unit = useMemo(
		() => unitData.byId(item.unitId),
		[item.unitId, unitData.byId]
	)

	return (
		<PageCard.Root>
			<PageCard.Header
				title="Informasi Bahan"
				actions={
					<PageCard.HeaderAction asChild>
						<Link from={Route.fullPath} to="/inventory/master/$id/edit">
							<Pencil />
							Edit
						</Link>
					</PageCard.HeaderAction>
				}
			/>
			<PageCard.ContentRow title="ID" value={id} />
			<PageCard.ContentRow title="Nama Bahan" value={item.name} />
			<PageCard.ContentRow
				title="Tipe"
				value={<ItemMasterTypeBadge type={item.type} />}
			/>
			<PageCard.ContentRow title="Kategori" value={category?.name} />
			<PageCard.ContentRow
				title="Default Cost"
				value={`${toIdr(item.baseCost)}`}
			/>
			<PageCard.ContentRow
				title="Stock Alert"
				value={
					item.alert?.enabled
						? `Aktif (min ${item.alert.min} ${unit?.base})`
						: 'Tidak aktif'
				}
			/>
		</PageCard.Root>
	)
}

function UnitSection() {
	const { item } = useItem()

	return (
		<PageCard>
			<PageCard.Header title="Konversi Operasional" />
			<UnitConversionPreviewTable unitId={item.unitId} />
		</PageCard>
	)
}

function OutletSection() {
	const { item } = useItem()
	const { outletData = [] } = item

	return (
		<PageCard.Root>
			<PageCard.Header title="Outlet" />
			<Table>
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell>Outlet</Table.HeaderCell>
						<Table.HeaderCell>Jenis</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{outletData.length === 0 && (
						<Table.Row>
							<Table.Cell colSpan={2}>
								<Text>Bahan ini belum di tambahkan ke outlet manapun</Text>
							</Table.Cell>
						</Table.Row>
					)}
					{outletData.map(({ id, name, type }) => {
						return (
							<Table.Row key={id}>
								<Table.Cell>{name}</Table.Cell>
								<Table.Cell>
									<p className="text-ui-fg-subtle">{type}</p>
								</Table.Cell>
							</Table.Row>
						)
					})}
				</Table.Body>
			</Table>
		</PageCard.Root>
	)
}

function RecipeSection() {
	const { item } = useItem()

	return (
		<PageCard.Root>
			<PageCard.Header
				title="Resep"
				desc="Hanya berlaku untuk bahan dengan tipe semi"
			/>
			<PageCard.ContentSection>
				{!item.recipeId && (
					<Alert variant="error" className="col-span-full">
						<p>Tidak ada resep yang ditentukan</p>
					</Alert>
				)}

				<Button asChild>
					<Link from={Route.fullPath} to="/inventory/master/$id/recipe">
						{item.recipeId ? 'Ubah' : 'Tambah'} Resep
					</Link>
				</Button>
			</PageCard.ContentSection>
		</PageCard.Root>
	)
}

function useItem() {
	const { id } = Route.useParams()
	const {
		data: { item },
	} = useSuspenseQuery(itemMasterDetail.qry({ id }))

	return {
		id,
		item,
	}
}
